import assert from 'assert';
import React, {useCallback, useEffect, useMemo} from 'react';
import isEqual from 'react-fast-compare';
import {shallowEqual} from 'react-redux';

import {makeReactContext} from 'lib/makeReactContext';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {createTableModelSelector, TableModel} from 'notebook/tables/store/models/TableModel';
import {createTableEditableStore, TableEditableStore} from 'notebook/tables/store/TableStore';

interface TableContextInterface {
  useTableEditableStore: ReturnType<typeof createTableEditableStore>;
  tableModelSelector: (state: TableEditableStore) => TableModel;
  setState: (state: Partial<TableEditableState>) => void;
}

export const TableModelContext = makeReactContext<TableContextInterface>(() => {
  assert(false, 'TableContext must be used within a TableStateWrapper');
}, 'StructuredTableContext');

export const TableModelProvider = function ({
  children,
  tableProps,
}: {
  children: React.ReactNode;
  tableProps: TableEditableProps;
}): JSX.Element {
  const {table, staleRows, errorRows, warningRows, computedTable, ...stableProps} = tableProps;
  const volatileProps = useMemo(
    () => ({table, staleRows, errorRows, warningRows, computedTable}),
    [table, staleRows, errorRows, warningRows, computedTable]
  );

  // Extract editor properties from context to store separately.
  const editorContext = EditorPropertiesContext.useContext();

  // This should explicitly only be created once, and use the initial prop
  const useTableEditableStore = useMemo(
    () => createTableEditableStore({stableProps, volatileProps, editorContext}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {setTableState, setTableProps, setEditorContext} = useTableEditableStore((store) => ({
    setTableState: store.setState,
    setTableProps: store.setProps,
    setEditorContext: store.setEditorContext,
  }));

  const tableModelSelector = useMemo(() => createTableModelSelector(), []);

  const setState = useCallback(
    (state: Partial<TableEditableState>) => {
      const previousState = useTableEditableStore.getState().state;
      const newState = {
        ...previousState,
        ...state,
      };
      if (!isEqual(newState, previousState)) {
        setTableState(newState);
      }
    },
    [setTableState, useTableEditableStore]
  );
  const contextValue: TableContextInterface = useMemo(
    () => ({tableModelSelector, setState, useTableEditableStore}),
    [tableModelSelector, setState, useTableEditableStore]
  );

  useEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (storeState.volatileProps !== volatileProps || !shallowEqual(storeState.stableProps, stableProps)) {
      setTableProps(stableProps, volatileProps);
    }
  }, [useTableEditableStore, setTableProps, volatileProps, stableProps]);

  useEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (!isEqual(storeState.editorContext, editorContext)) {
      setEditorContext(editorContext);
    }
  }, [useTableEditableStore, setEditorContext, editorContext]);

  return <TableModelContext.Provider value={contextValue}>{children}</TableModelContext.Provider>;
};
